import { downloadFile, updateSearchParam } from '@fukuri-admins/utils/common';
import { ComponentProps, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchServiceUsage, fetchServiceUsageCSV } from '~/api/service_usages';
import { useAuth } from '~/components/auth/AuthProvider';
import {
  ActionTableSortParamsType,
  defaultActionTableSortParams,
} from '~/types/table/ActionTableSortParamsType';
import { handleSorting } from '~/utils/sortings';
import { convertToServiceUsageDisplayType } from '../logics/convertToServiceUsageDisplayType';
import { ServiceReportPresentation } from '../presentations';

type ServiceUsageDisplayType = ComponentProps<
  typeof ServiceReportPresentation
>['filteredServiceApplications'][number];

type IUseServiceReport = () => {
  state: {
    filteredServiceApplications: ServiceUsageDisplayType[];
    loading: boolean;
    currentPage: number;
    totalPage: number;
    totalCount: number;
  };
  actions: {
    handleOutputCsv: () => Promise<void>;
    handlePageChange: (page: number) => void;
    handleHeaderSort: (column: string) => void;
  };
};

export const useServiceReport: IUseServiceReport = () => {
  const [sortParams, setSortParams] = useState<ActionTableSortParamsType>(
    defaultActionTableSortParams
  );
  const [filteredServiceApplications, setFilteredServiceApplications] = useState<
    ServiceUsageDisplayType[]
  >([]);
  const { signOut } = useAuth();
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = Number(searchParams.get('page') ?? 1);
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);

  const handleOutputCsv = async () => {
    const { data } = await fetchServiceUsageCSV(signOut);
    if (data instanceof Blob) {
      downloadFile(data, 'service_usages.csv');
    }
  };

  const getServiceUsagesData = async (page?: number) => {
    const pageNumber = page ?? null;
    const { data: serviceApplicationsData, headers } = await fetchServiceUsage(signOut, {
      ...(pageNumber && { page: pageNumber.toString() }),
    });
    setTotalPage(Number(headers['total-pages']));
    setTotalCount(Number(headers['total-count']));

    if (!('service_usages' in serviceApplicationsData)) {
      throw new Error('Unexpected data format');
    }

    const processedServiceApplicationsData = serviceApplicationsData.service_usages.map(
      convertToServiceUsageDisplayType
    );
    setFilteredServiceApplications(processedServiceApplicationsData);
    setLoading(false);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSearchParams(updateSearchParam(searchParams, 'page', page.toString()));
    getServiceUsagesData(page);
  };

  const handleHeaderSort = (column: string) => {
    handleSorting(
      column,
      sortParams,
      filteredServiceApplications,
      setSortParams,
      setFilteredServiceApplications
    );
  };

  useEffect(() => {
    getServiceUsagesData(initialPage > 1 ? initialPage : undefined);
  }, []);

  return {
    state: {
      filteredServiceApplications,
      loading,
      currentPage,
      totalPage,
      totalCount,
    },
    actions: {
      handleOutputCsv,
      handlePageChange,
      handleHeaderSort,
    },
  };
};
